import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import spayd from 'spayd'
import useSWR from 'swr'

import { BadgeCheck, CircleAlert, CreditCard, Package, RotateCw } from 'lucide-react'

import { KeyValueList, Money, PageHeader, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Loading } from '@/components/Loading'
import { QrCode } from '@/components/QrCode'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import API from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { computeIBANFromCzechBankAccount } from '@/lib/computeIBANFromCzechBankAccount'
import { formatDate } from '@/lib/formats/formatDate'
import { hasRole } from '@/lib/hasRole'
import { Role } from '@/types/User'

export default function SubscriptionPage() {
  const navigate = useNavigate()

  const { user, isLoading } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'Subscription_getServerSideProps',
  })

  const { t } = useTranslation(undefined, {
    keyPrefix: 'subscription',
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const title = t('title')

  const {
    data: currentSubscription,
    isLoading: isLoadingCurrentSubscription,
    error,
    mutate: mutateCurrent,
  } = useSWR<{
    id: string
    status: string
    startDate: string | null
    endDate: string | null
    plan: {
      id: string
      name: string
      status: string
    }
  } | null>('/api/v2/subscriptions', apiFetcher)

  const {
    data: plans,
    isLoading: isLoadingPlans,
    mutate,
  } = useSWR<
    {
      id: string
      name: string
      price: {
        amount: string
        currency: string
      }
      duration: string
      status: string
      orders: {
        orderId: string
        subscriptionId: string
        paid: boolean
        vs: string
        accountNumber: string
        iban: string
        price: {
          amount: string
          currency: string
        }
      }[]
    }[]
  >('/api/v2/subscription-plans', apiFetcher)

  const handleChangePlan = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    const planId = event.currentTarget.dataset.planId

    setIsProcessing(true)

    API.post('/api/v2/subscriptions', {
      subscriptionPlanId: planId,
    })
      .then(() => {
        mutate()
        mutateCurrent()
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!user || !hasRole(user.roles, [Role.owner, Role.manager])) {
      navigate('/')
    }
  }, [user, navigate, isLoading])

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/settings',
        children: t('title'),
      },
    ],
  })

  if (isLoading) {
    return <Loading />
  }

  const activePlans = plans?.filter((plan) => plan.status === 'active') ?? []

  return (
    <div className="mx-auto w-full max-w-3xl">
      <title>{title}</title>

      <PageHeader
        title={title}
        description="Tarif invo.space a platby za něj. Platí se převodem, po připsání platby se předplatné prodlouží samo."
      />

      <section className="mb-8">
        <h2 className="mb-3 text-[15px] font-medium">{t('currentPlan')}</h2>
        {isLoadingCurrentSubscription && (
          <Skeleton className="h-24 w-full rounded-lg" />
        )}
        {!isLoadingCurrentSubscription && error && (
          <EmptyState
            icon={CircleAlert}
            title="Předplatné se nepodařilo načíst"
            description="Zkuste to prosím za chvíli znovu. Pokud chyba trvá, napište nám přes Zpětnou vazbu v menu účtu."
            ctaButton={
              <Button
                variant="outline"
                size="sm"
                onClick={() => mutateCurrent()}
              >
                <RotateCw />
                Zkusit znovu
              </Button>
            }
          />
        )}
        {!isLoadingCurrentSubscription && !error && currentSubscription && (
          <div className="rounded-lg border p-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="grid size-10 place-items-center rounded-lg bg-brand-soft text-brand-strong">
                <BadgeCheck className="size-5" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium">
                  {currentSubscription.plan.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {currentSubscription.endDate
                    ? `Platí do ${formatDate(new Date(currentSubscription.endDate))}`
                    : 'Bez data konce'}
                </p>
              </div>
              <StatusBadge status="active" label="Aktivní" />
            </div>
            <KeyValueList
              className="mt-4"
              items={[
                {
                  label: 'Začátek',
                  value: currentSubscription.startDate
                    ? formatDate(new Date(currentSubscription.startDate))
                    : '-',
                },
                {
                  label: 'Konec',
                  value: currentSubscription.endDate
                    ? formatDate(new Date(currentSubscription.endDate))
                    : '-',
                },
              ]}
            />
          </div>
        )}
        {!isLoadingCurrentSubscription && !error && !currentSubscription && (
          <EmptyState
            icon={CreditCard}
            title="Zatím nemáte předplatné"
            description={
              activePlans.length > 0
                ? 'Vyberte si tarif níže. Ukážeme vám platební údaje a QR kód, po připsání platby se předplatné aktivuje samo.'
                : 'Nabídka tarifů se teď nedá načíst. Zkuste to později, nebo nám napište přes Zpětnou vazbu v menu účtu.'
            }
          />
        )}
      </section>

      {(isLoadingPlans || activePlans.length > 0 || currentSubscription) && (
        <section>
          <h2 className="mb-3 text-[15px] font-medium">{t('changePlan')}</h2>
          {isLoadingPlans && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Skeleton className="h-36 rounded-lg" />
              <Skeleton className="h-36 rounded-lg" />
            </div>
          )}
          {!isLoadingPlans && activePlans.length === 0 && (
            <EmptyState
              icon={Package}
              title="Žádné tarify k dispozici"
              description="Nabídka tarifů se teď nedá načíst. Zkuste to později nebo nám napište přes Zpětnou vazbu."
            />
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {activePlans.map((plan) => {
              const unpaidOrders = plan.orders.filter((order) => !order.paid)
              const isCurrent = currentSubscription?.plan.id === plan.id

              return (
                <div
                  key={plan.id}
                  className="flex flex-col rounded-lg border p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-medium">{plan.name}</p>
                    {isCurrent && (
                      <StatusBadge status="active" label="Současný" />
                    )}
                  </div>
                  <p className="mt-2 flex items-baseline gap-1.5">
                    <Money
                      amount={plan.price.amount}
                      currency={plan.price.currency}
                      size="lg"
                    />
                    <span className="text-sm text-muted-foreground">
                      / {plan.duration} {t('days')}
                    </span>
                  </p>

                  {unpaidOrders.length === 0 && (
                    <Button
                      className="mt-5 self-start"
                      variant={isCurrent ? 'outline' : 'default'}
                      data-plan-id={plan.id}
                      onClick={handleChangePlan}
                      disabled={isProcessing}
                    >
                      {isCurrent ? t('prolongPlan') : t('subscribe')}
                    </Button>
                  )}

                  {unpaidOrders.map((order) => {
                    const [accountNumber, bankCode] =
                      order.accountNumber.split('/')
                    const [prefix, number] = accountNumber.includes('-')
                      ? accountNumber.split('-')
                      : ['', accountNumber]
                    const iban =
                      order.iban ||
                      computeIBANFromCzechBankAccount(prefix, number, bankCode)
                    const amount = Number(order.price.amount) / 100
                    const spaydString = spayd({
                      acc: iban,
                      am: amount.toFixed(2),
                      cc: order.price.currency,
                      xvs: order.vs,
                      msg: 'invo.space',
                    })

                    return (
                      <div
                        key={order.orderId}
                        className="mt-5 rounded-lg bg-muted/70 p-4"
                      >
                        <p className="mb-2 text-[13px] font-medium">
                          Čeká na platbu
                        </p>
                        <div className="flex flex-wrap items-start gap-4">
                          <dl className="min-w-[200px] flex-1 divide-y text-sm">
                            {[
                              {
                                label: 'Částka',
                                value: <Money amount={order.price.amount} currency={order.price.currency} size="sm" />,
                                copy: amount.toFixed(2).replace('.', ','),
                              },
                              { label: 'Účet', value: order.accountNumber, copy: order.accountNumber },
                              { label: 'VS', value: order.vs, copy: order.vs },
                              ...(order.iban ? [{ label: 'IBAN', value: order.iban, copy: order.iban }] : []),
                            ].map((row) => (
                              <div key={row.label} className="flex items-center justify-between gap-3 py-1.5">
                                <dt className="text-muted-foreground">{row.label}</dt>
                                <dd className="flex items-center gap-1 text-right tabular break-all">
                                  {row.value}
                                  <CopyButton content={row.copy} aria-label={`Kopírovat ${row.label}`} variant="ghost" className="size-7 text-muted-foreground" />
                                </dd>
                              </div>
                            ))}
                          </dl>
                          <div className="mx-auto shrink-0 rounded-md bg-white p-1.5">
                            <QrCode spaydString={spaydString} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}
